import { useEffect, useState } from 'react';

/**
 * Parsed article route. Example hash: "#/article/amld/6" -> { actId: "amld", num: "6" }
 */
export type ArticleRoute = { actId: string; num: string };

export function parseArticleRoute(hash: string): ArticleRoute | null {
  // Accept "#/article/<id>/<num>" with optional trailing slash
  const m = hash.match(/^#\/article\/([a-z]+)\/([^/]+)\/?$/i);
  if (!m) return null;
  return { actId: m[1].toLowerCase(), num: m[2] };
}

export function articleHashFor(actId: string, num: string | number): string {
  return `#/article/${actId}/${num}`;
}

/**
 * Parsed glossary route. Matches both "#/glossary" (open whole modal)
 * and "#/glossary/<entry-id>" (deep link to specific entry).
 * Returns the entry id, or empty string if just "#/glossary".
 * Returns null if no glossary route.
 */
export function parseGlossaryRoute(hash: string): string | null {
  const m = hash.match(/^#\/glossary(?:\/([a-z0-9\-]+))?\/?$/i);
  if (!m) return null;
  return (m[1] || '').toLowerCase();
}

export function glossaryHashFor(entryId?: string): string {
  return entryId ? `#/glossary/${entryId}` : '#/glossary';
}

/**
 * Parsed mandate route. Example: "#/mandate/rts_amld_53_10" -> { id: "rts_amld_53_10" }
 * Optional article focus: "#/mandate/rts_amld_53_10/art/2".
 */
export type MandateRoute = { id: string; art?: string };

export function parseMandateRoute(hash: string): MandateRoute | null {
  const m = hash.match(/^#\/mandate\/([a-z0-9_]+)(?:\/art\/([^/]+))?\/?$/i);
  if (!m) return null;
  return { id: m[1].toLowerCase(), art: m[2] };
}

export function mandateHashFor(id: string, art?: string | number): string {
  return art != null ? `#/mandate/${id}/art/${art}` : `#/mandate/${id}`;
}

/**
 * Returns the current hash and re-renders on `hashchange`.
 */
export function useHashRoute(): string {
  const [hash, setHash] = useState<string>(typeof window !== 'undefined' ? window.location.hash : '');
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return hash;
}
